import { useMemo, useState } from "react";

import {
  faBracketsCurly,
  faChevronDown,
  faSearch,
  faText,
} from "@fortawesome/pro-regular-svg-icons";
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from "@headlessui/react";

import { Input } from "src/base-components/Input";
import { Icon } from "src/design-system/Icon";
import { Pill } from "src/design-system/Pill";
import { Tooltip } from "src/design-system/Tooltip";
import {
  CaseSchemaDisplay,
  JsonSchemaProperty,
  ParsedSchemaSection,
} from "src/schema/schemaEditor/caseSchemaUtils";
import { TYPE_ICONS } from "src/utils/constants";

interface CaseSchemaHintProps {
  selectedSchema: CaseSchemaDisplay;
  rootPath: string;
}

const CaseSchemaPathPill = ({ path }: { path: string }) => {
  const [copied, setCopied] = useState(false);
  return (
    <Tooltip placement="top" title={copied ? "Copied!" : path} asChild>
      <Pill
        size="sm"
        variant="indigo-light"
        maxWidth
        onClick={(e) => {
          e.stopPropagation();
          navigator.clipboard.writeText(path);
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        }}
      >
        <Pill.Icon icon={faBracketsCurly} />
        <Pill.Text>{path}</Pill.Text>
      </Pill>
    </Tooltip>
  );
};

const PropertyPill = ({
  property,
  schema,
}: {
  property: string;
  schema: JsonSchemaProperty;
}) => {
  const typeIcon = TYPE_ICONS[schema.type as keyof typeof TYPE_ICONS] || faText;
  return (
    <Pill fontType="code" size="sm" variant="gray">
      <Pill.Icon icon={typeIcon} />
      <Pill.Text>{property}</Pill.Text>
    </Pill>
  );
};

const PropertyContainer: React.FC<{
  properties: [string, JsonSchemaProperty][];
}> = ({ properties }) => (
  <div className="mb-5 ml-3.5 flex max-w-full flex-row flex-wrap gap-x-1 gap-y-1">
    {properties.map(([key, schema]) => (
      <PropertyPill key={key} property={key} schema={schema} />
    ))}
  </div>
);

const SchemaSection: React.FC<{
  section: ParsedSchemaSection;
  searchTerm: string;
}> = ({ section, searchTerm }) => {
  const matchesSearch = (text: string) =>
    text.toLowerCase().includes(searchTerm.toLowerCase());

  const filteredProperties = searchTerm
    ? Object.entries(section.properties).filter(
        ([key]) => matchesSearch(key) || matchesSearch(section.name),
      )
    : Object.entries(section.properties);

  if (
    searchTerm &&
    filteredProperties.length === 0 &&
    !matchesSearch(section.name)
  ) {
    return null;
  }

  return (
    <div className="items-top flex w-full">
      <div className="mt-2.5 h-[1px] w-3.5 flex-shrink-0 bg-gray-200" />
      <div className="min-w-0 flex-1">
        <Disclosure as="div" className="w-full max-w-full">
          <DisclosureButton className="group w-full max-w-full outline-none">
            <div className="ml-0.5 flex flex-row items-center gap-x-2 overflow-hidden text-left">
              <div className="-rotate-90 transition-transform duration-300 group-data-[open]:rotate-0">
                <Icon icon={faChevronDown} size="sm" />
              </div>
              <div className="flex-1 font-inter-semibold-13px text-gray-800">
                {section.name}
              </div>
              <div className="min-w-0 flex-shrink">
                <CaseSchemaPathPill path={section.path} />
              </div>
            </div>
          </DisclosureButton>
          <DisclosurePanel className="relative pl-3">
            <div className="absolute top-1 bottom-[14px] left-3 w-[1px] border-gray-200 border-l" />
            <div className="mt-2">
              <PropertyContainer properties={filteredProperties} />
            </div>
          </DisclosurePanel>
        </Disclosure>
      </div>
    </div>
  );
};

export const CaseSchemaHint: React.FC<CaseSchemaHintProps> = ({
  selectedSchema,
  rootPath,
}) => {
  const [searchTerm, setSearchTerm] = useState("");

  const filteredData = useMemo(() => {
    if (!searchTerm) {
      return {
        primitiveProperties: Object.entries(selectedSchema.primitiveProperties),
        nestedSections: selectedSchema.nestedSections,
      };
    }

    const matchesSearch = (text: string) =>
      text.toLowerCase().includes(searchTerm.toLowerCase());

    const filteredPrimitiveProperties = Object.entries(
      selectedSchema.primitiveProperties,
    ).filter(([key]) => matchesSearch(key));

    const filteredNestedSections = selectedSchema.nestedSections.filter(
      (section) => {
        const hasMatchingProperty = Object.keys(section.properties).some(
          (key) => matchesSearch(key),
        );
        return matchesSearch(section.name) || hasMatchingProperty;
      },
    );

    return {
      primitiveProperties: filteredPrimitiveProperties,
      nestedSections: filteredNestedSections,
    };
  }, [selectedSchema, searchTerm]);

  return (
    <div className="rounded-md border border-gray-100 bg-gray-50 px-4 py-4">
      <div className="mb-4">
        <Input
          data-loc="case-schema-hint-search"
          placeholder="Search a property..."
          prefixIcon={{ icon: faSearch }}
          value={searchTerm}
          fullWidth
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <Disclosure
        as="div"
        className="w-full max-w-full"
        data-loc="case-schema-hint"
        defaultOpen
      >
        <DisclosureButton className="group w-full max-w-full outline-none">
          <div className="ml-0.5 flex flex-row items-center gap-x-2 overflow-hidden text-left">
            <div className="-rotate-90 transition-transform duration-300 group-data-[open]:rotate-0">
              <Icon icon={faChevronDown} size="sm" />
            </div>
            <div
              className="flex flex-1 items-center gap-x-2 font-inter-semibold-13px text-gray-800"
              data-loc="case-schema-hint-pill-container"
            >
              Case
              <Pill size="sm" variant="gray">
                <Pill.Icon icon={selectedSchema.icon} />
                <Pill.Text>{selectedSchema.name}</Pill.Text>
              </Pill>
            </div>
            <div className="min-w-0 flex-shrink">
              <CaseSchemaPathPill path={rootPath} />
            </div>
          </div>
        </DisclosureButton>
        <DisclosurePanel className="relative pl-3">
          <div className="absolute top-1 bottom-[14px] left-3 w-[1px] border-gray-200 border-l" />
          <div className="mt-2">
            {filteredData.primitiveProperties.length > 0 && (
              <PropertyContainer
                properties={filteredData.primitiveProperties}
              />
            )}

            {filteredData.nestedSections.map((section) => (
              <SchemaSection
                key={section.id}
                searchTerm={searchTerm}
                section={section}
              />
            ))}
          </div>
        </DisclosurePanel>
      </Disclosure>
    </div>
  );
};
